import React from "react";
import { Link } from "react-router-dom";
import Seo from "@/components/Seo";
import { ShieldCheck } from "lucide-react";

export const TERMS = [
  {
    t: "Staff deployment & reporting",
    d: "Every chef or household staff member placed by Chef Hire remains on Chef Hire's payroll and reports directly to Chef Hire. Salaries, statutory contributions, conduct, discipline and performance management are held by Chef Hire — the client never becomes the employer of record.",
  },
  {
    t: "Our fee",
    d: "Chef Hire charges a one-time placement fee equal to one month's salary of the placed staff member, plus 18% GST on that fee. The monthly salary itself is billed through Chef Hire. There is no percentage commission.",
  },
  {
    t: "Trials",
    d: "Staff trials are chargeable at ₹2,500–₹5,000 per day depending on the chef's profile and seniority, and must be booked at least 2 days in advance. Trial fees are non-refundable and are not adjusted against the placement fee.",
  },
  {
    t: "Replacement guarantee",
    d: "If a placed staff member leaves mid-engagement, Chef Hire will provide a replacement chef of comparable calibre within 1 week. A standby chef is deployed in the interim so your household is never without service.",
  },
  {
    t: "Scope of duties",
    d: "Duties are fixed at the time of appointment. Anything added later that was not part of the agreed brief is chargeable, assessed on the extent of additional work actually performed. Overtime and additional guest counts are quoted before execution.",
  },
  {
    t: "Agreement term",
    d: "Engagements are governed by an 11-month agreement, renewable on mutual consent at the end of the term. Renewal terms, including salary revision, are reviewed 30 days before expiry.",
  },
  {
    t: "Payment terms",
    d: "The placement fee plus GST is payable upfront to confirm a booking. Monthly salary invoices are raised in advance of each cycle and are due within 7 days. Delayed payment may result in suspension of deployment.",
  },
  {
    t: "Leave, holidays & working hours",
    d: "Staff are entitled to one weekly off and declared national holidays unless alternate arrangements are agreed in writing. Standard duty is up to 9 hours a day. Hours beyond this are treated as overwork and billed accordingly.",
  },
  {
    t: "Cancellation & refunds",
    d: "Cancellation more than 7 days before the start date attracts a 25% retention of the placement fee. Within 7 days, 50% is retained. Once the staff member has joined, the placement fee is non-refundable, with the replacement guarantee applying instead.",
  },
  {
    t: "Confidentiality & conduct",
    d: "All staff are background-verified and NDA-bound. Household privacy, guest identities and residence details are treated as strictly confidential. Clients agree to provide a safe, respectful working environment.",
  },
  {
    t: "Non-solicitation",
    d: "Clients agree not to directly employ, or employ through a third party, any staff introduced by Chef Hire during the engagement or for 12 months thereafter. Breach attracts a fee equal to three months' salary of that staff member.",
  },
  {
    t: "Kitchen, provisions & travel",
    d: "The client provides the kitchen, equipment, utilities and groceries unless a provisioned package is agreed. Travel outside the serviced city, accommodation and local conveyance for out-station assignments are billed at actuals.",
  },
  {
    t: "Liability",
    d: "Chef Hire's aggregate liability for any claim is limited to the placement fee received for that engagement. Chef Hire is not liable for indirect or consequential loss, or for outcomes arising from client-supplied ingredients, equipment or instructions.",
  },
  {
    t: "Governing law",
    d: "These terms are governed by the laws of India. Disputes are subject to arbitration in Indore, Madhya Pradesh, under the Arbitration and Conciliation Act, 1996.",
  },
];

export default function Terms() {
  return (
    <div className="max-w-4xl mx-auto px-6 py-20" data-testid="terms-page">
      <Seo
        title="Terms & Conditions · Chef Hire"
        description="Chef Hire engagement terms — one month's salary as placement fee plus GST, staff on Chef Hire payroll, replacement guarantee, trials, 11-month renewable agreement."
        path="/terms"
      />
      <div className="small-caps text-[10px] text-neutral-500">Legal</div>
      <h1 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl mt-3">Terms &amp; Conditions</h1>
      <p className="text-neutral-400 mt-5 max-w-2xl leading-relaxed">
        These terms govern every private chef and household staff engagement arranged by Chef Hire.
        By confirming a booking you accept them.
      </p>

      <div className="mt-8 border border-[#F5B041]/30 bg-[#F5B041]/5 p-6 flex gap-4">
        <ShieldCheck size={18} className="text-[#F5B041] shrink-0 mt-0.5" />
        <p className="text-sm text-neutral-300 leading-relaxed">
          No 10% commission. Chef Hire charges a one-time fee equal to <span className="text-[#F5B041]">one month's salary</span> plus
          18% GST. The staff member stays on <span className="text-[#F5B041]">Chef Hire's payroll</span> and reports to us.
        </p>
      </div>

      <ol className="mt-14 space-y-10">
        {TERMS.map((c, i) => (
          <li key={c.t} data-testid={`term-${i + 1}`}>
            <div className="flex items-baseline gap-4">
              <span className="font-serif-display text-2xl text-[#C04000] w-8 shrink-0">{String(i + 1).padStart(2, "0")}</span>
              <div>
                <h2 className="font-serif-menu text-base md:text-lg text-white">{c.t}</h2>
                <p className="text-neutral-400 text-sm mt-2 leading-relaxed">{c.d}</p>
              </div>
            </div>
          </li>
        ))}
      </ol>

      <div className="dotted-sep my-14" />
      <p className="text-neutral-500 text-xs leading-relaxed">
        Last updated {new Date().toLocaleDateString("en-IN", { month: "long", year: "numeric" })}. For clarifications write to{" "}
        <a href="mailto:hello@chefhire.in" className="text-[#F5B041] hover:underline">hello@chefhire.in</a> or call{" "}
        <a href="tel:+917971413816" className="text-[#F5B041] hover:underline">+91 79714 13816</a>.{" "}
        <Link to="/" className="hover:text-white">Back home →</Link>
      </p>
    </div>
  );
}
